import { Button, IconButton } from "@chakra-ui/button";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { CherryHubAdminApi } from "../api/adminApiModels";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  HStack,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { SortableItem } from "../components/Dnd/SortableItem";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import { useSearchInput } from "./useSearchInput";
import { cloneDeep } from "lodash";
import { v4 as uuid } from "uuid";
import { Dropable } from "../components/Dnd/Dropable";
import { KvpFormField } from "../forms/RegistrationFormConfigurationDetailForm";
import { getFormFieldId } from "../forms/RegistrationFormConfigurationDetailForm";

interface PromptState {
  resolve: (result: {
    ok: boolean;
    customFields?: CherryHubAdminApi.CustomFormField[];
  }) => void;
  reject: () => void;
  formFields: KvpFormField | null;
  action?: string;
}

function sanitizeId(id: string) {
  return id.replace("Left_", "").replace("Right_", "");
}

const getFormFieldName = (formField: CherryHubAdminApi.CustomFormField) => {
  if (
    formField.FieldSpecification?.IsCustomIntegrationField ||
    formField.FieldSpecification?.IsNonIntegratedField
  ) {
    return formField.Name;
  }

  return formField.FieldSpecification?.SurrogateKey!;
};

export function useRegistrationCustomFieldModal() {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [activePrompt, setActivePrompt] = useState<PromptState | null>(null);
  const [activeId, setActiveId] =
    useState<CherryHubAdminApi.CustomFormField | null>(null);
  const [formFieldsToBeAdded, setFormFieldsToBeAdded] = useState<
    CherryHubAdminApi.CustomFormField[]
  >([]);

  const kvpFormFields = cloneDeep(activePrompt?.formFields) || [];
  let formFields: CherryHubAdminApi.CustomFormField[] = [];
  kvpFormFields.forEach((field) => {
    formFields = formFields.concat(field.value.map((val) => val));
  });
  const formFieldsKeys = formFields.map(
    (field) => field.FieldSpecification?.SurrogateKey ?? field.Identifier
  );

  const onClickOk = useCallback(() => {
    if (activePrompt) {
      activePrompt.resolve({ ok: true, customFields: formFieldsToBeAdded });
      setActivePrompt(null);
      setFormFieldsToBeAdded([]);
    }
  }, [activePrompt, setActivePrompt, formFieldsToBeAdded]);

  const onCancel = useCallback(() => {
    if (activePrompt) {
      activePrompt.resolve({ ok: false });
      setActivePrompt(null);
      setFormFieldsToBeAdded([]);
    }
  }, [activePrompt, setActivePrompt]);

  const filteredKvpFormFields = () => {
    const results = cloneDeep(kvpFormFields);
    kvpFormFields.forEach((kvp, idxKvp) => {
      kvp.value.forEach((field, idxField) => {
        if (
          formFieldsToBeAdded.find(
            (added) => getFormFieldId(added) == getFormFieldId(field)
          )
        ) {
          if (kvp.value.length == results[idxKvp].value.length) {
            results[idxKvp].value.splice(idxField, 1);
          } else {
            const resultsIdx = results[idxKvp].value.findIndex(
              (result) => getFormFieldId(result) == getFormFieldId(field)
            );
            results[idxKvp].value.splice(resultsIdx, 1);
          }
        }
      });
    });
    return results.filter((field) => field.value.length);
  };

  const handleDragStart = (event: any) => {
    const { active } = event;
    const customField =
      formFieldsToBeAdded.find(
        (df) => getFormFieldId(df) == sanitizeId(active.id)
      ) ||
      formFields.find((df) => getFormFieldId(df) == sanitizeId(active.id)) ||
      null;
    setActiveId(customField);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    const removeCF = () => {
      if (active.id.indexOf("Right_") != -1) {
        const deletedCF = formFieldsToBeAdded.find(
          (cf) => getFormFieldId(cf) == sanitizeId(active.id)
        );
        if (deletedCF) {
          removeCustomField(deletedCF);
        }
      }
    };

    if (!over || !over.id) {
      removeCF();
      return setActiveId(null);
    }

    if (active.id == over.id) {
      return setActiveId(null);
    }

    if (over.id.indexOf("Left_") != -1) {
      if (active.id.indexOf("Left_") != -1) {
        return setActiveId(null);
      }

      removeCF();
    } else if (over.id.indexOf("Right_") != -1) {
      setFormFieldsToBeAdded((items) => {
        if (active.id.indexOf("Left_") != -1) {
          var newItem = formFields?.find(
            (df) => getFormFieldId(df) == sanitizeId(active.id)
          );
          if (newItem) {
            if (
              newItem.FieldSpecification?.IsNonIntegratedField ||
              newItem.FieldSpecification?.IsCustomIntegrationField
            ) {
              newItem = cloneDeep(newItem);
              newItem.Identifier = uuid();
              newItem.IsVisible = true;
            }
            items.push(newItem);
          }
        }
        const oldIndex = items.findIndex(
          (item) => getFormFieldId(item) == sanitizeId(active.id)
        );
        const newIndex = items.findIndex(
          (item) => getFormFieldId(item) == sanitizeId(over.id)
        );
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    setActiveId(null);
  };

  const addCustomField = (customField: CherryHubAdminApi.CustomFormField) => {
    const newCustomFields = cloneDeep(formFieldsToBeAdded);
    const newCustomField = cloneDeep(customField);
    if (
      newCustomField.FieldSpecification?.IsNonIntegratedField ||
      newCustomField.FieldSpecification?.IsCustomIntegrationField
    ) {
      newCustomField.Identifier = uuid();
      newCustomField.IsVisible = true;
    }
    newCustomFields.push(newCustomField);
    setFormFieldsToBeAdded(newCustomFields);
  };

  const removeCustomField = (
    customField: CherryHubAdminApi.CustomFormField
  ) => {
    setFormFieldsToBeAdded(
      formFieldsToBeAdded.filter(
        (field) => getFormFieldId(field) != getFormFieldId(customField)
      )
    );
  };

  const {
    debouncedQuery,
    isDebouncing,
    onChange: onChangeSearch,
    query,
    element: searchInputElement,
  } = useSearchInput({ placeholder: "Search field...", mb: 2 });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const modalElement = useMemo(() => {
    return (
      <Modal
        isOpen={!!activePrompt}
        onClose={onCancel}
        initialFocusRef={cancelRef}
        size="4xl"
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>Add Field</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto" overflowX="hidden" maxH="700px">
              <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <HStack w="100%" alignItems="flex-start" spacing={8}>
                  <SortableContext
                    items={formFields.map(
                      (field) => `Left_${getFormFieldId(field)}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <VStack w="50%" alignItems="flex-start" spacing={0.5}>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        mb={4}
                        color="cherryGrey.900"
                      >
                        Available Fields
                      </Text>
                      {searchInputElement}
                      <Accordion
                        allowToggle
                        allowMultiple
                        defaultIndex={Array(activePrompt?.formFields?.length)
                          .fill(0)
                          .map((num, idx) => idx)}
                        w="100%"
                      >
                        {filteredKvpFormFields()
                          ?.filter((field) =>
                            debouncedQuery
                              ? field.key
                                  .toLowerCase()
                                  .indexOf(debouncedQuery.toLowerCase()) !=
                                  -1 ||
                                field.value.filter(
                                  (f) =>
                                    getFormFieldName(f)
                                      ?.toLowerCase()
                                      .indexOf(
                                        debouncedQuery.toLocaleLowerCase()
                                      ) != -1
                                ).length > 0
                              : true
                          )
                          .map((kvp, kvpIdx) => (
                            <AccordionItem key={`accordion-${kvpIdx}`}>
                              <AccordionButton>
                                <HStack flex="1" textAlign="left" spacing={0.5}>
                                  <Text as="h2" fontSize="md">
                                    {kvp.value[0].Name}
                                  </Text>
                                  <Text as="span" color="red" fontSize="md">
                                    {kvp.value[0].FieldSpecification
                                      ?.IsSystemRequired && "*"}
                                  </Text>
                                </HStack>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel>
                                {kvp.value
                                  .filter((field) =>
                                    debouncedQuery
                                      ? field.FieldSpecification?.SurrogateKey?.toLowerCase().indexOf(
                                          debouncedQuery.toLocaleLowerCase()
                                        ) != -1
                                      : true
                                  )
                                  .map((field) => (
                                    <SortableItem
                                      key={getFormFieldId(field)}
                                      id={`Left_${getFormFieldId(field)}`}
                                      w="100%"
                                      border="1px solid var(--chakra-colors-cherryGrey-300)"
                                      minH="48px"
                                      borderRadius={3}
                                      bgColor="cherryGrey.40"
                                      px={4}
                                    >
                                      <HStack
                                        w="100%"
                                        maxW="calc(100% - var(--chakra-space-8))"
                                        alignItems="center"
                                      >
                                        <Text
                                          flexGrow={4}
                                          whiteSpace="normal"
                                          wordBreak="break-word"
                                        >
                                          {getFormFieldName(field)}
                                        </Text>
                                        <IconButton
                                          alignSelf="center"
                                          borderRadius="full"
                                          icon={<AddIcon />}
                                          aria-label="Add"
                                          title="Add"
                                          size="xs"
                                          onClick={() => addCustomField(field)}
                                          flexShrink={1}
                                        />
                                      </HStack>
                                    </SortableItem>
                                  ))}
                              </AccordionPanel>
                            </AccordionItem>
                          ))}
                      </Accordion>
                    </VStack>
                  </SortableContext>

                  <SortableContext
                    items={formFieldsToBeAdded.map(
                      (field) => `Right_${getFormFieldId(field)}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <VStack
                      spacing={0.5}
                      w="100%"
                      maxW="50%"
                      alignSelf="stretch"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        mb={4}
                        color="cherryGrey.900"
                      >
                        Fields To Be Added
                      </Text>
                      {formFieldsToBeAdded.length &&
                        formFieldsToBeAdded?.map((field) => (
                          <SortableItem
                            key={getFormFieldId(field)}
                            id={`Right_${getFormFieldId(field)}`}
                            w="100%"
                            border="1px solid var(--chakra-colors-cherryGrey-300)"
                            borderRadius={3}
                            px={4}
                            minH="48px"
                            bgColor="cherryGrey.40"
                          >
                            <HStack
                              w="100%"
                              maxW="calc(100% - var(--chakra-space-8))"
                              alignItems="center"
                            >
                              <Text
                                flexGrow={4}
                                whiteSpace="normal"
                                wordBreak="break-word"
                              >
                                {getFormFieldName(field)}
                              </Text>
                              {
                                <IconButton
                                  alignSelf="center"
                                  borderRadius="full"
                                  icon={<CloseIcon />}
                                  aria-label="Remove"
                                  title="Remove"
                                  size="xs"
                                  onClick={() => removeCustomField(field)}
                                />
                              }
                            </HStack>
                          </SortableItem>
                        ))}
                      {activeId && (
                        <Dropable
                          id={"Right_dropable"}
                          w="100%"
                          border="1px solid var(--chakra-colors-cherryGrey-300)"
                          borderRadius={3}
                          px={4}
                          bgColor="cherryGrey.40"
                          color="cherryGrey.700"
                          justifyContent="center"
                          flexGrow={1}
                        >
                          <Text>Drag some fields here.</Text>
                        </Dropable>
                      )}
                    </VStack>
                  </SortableContext>
                </HStack>
                <DragOverlay>
                  {activeId ? (
                    <SortableItem
                      id={getFormFieldId(activeId)}
                      w="100%"
                      border="1px solid var(--chakra-colors-cherryGrey-300)"
                      px={4}
                      bg="cherryGrey.40"
                      borderRadius={3}
                      minH="48px"
                    >
                      <Text>{getFormFieldName(activeId)}</Text>
                    </SortableItem>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </ModalBody>
            <ModalFooter>
              <Button ref={cancelRef} onClick={onCancel}>
                Cancel
              </Button>
              <Button colorScheme="cherryButton" ml={3} onClick={onClickOk}>
                {activePrompt?.action ?? "Add"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    );
  }, [
    activePrompt,
    cancelRef,
    onCancel,
    onClickOk,
    formFieldsToBeAdded,
    query,
    debouncedQuery,
    activeId,
  ]);

  const showCustomFieldModal = useCallback(
    (
      formFields: KvpFormField | null,
      action?: string
    ): Promise<{
      ok: boolean;
      customFields?: CherryHubAdminApi.CustomFormField[];
    }> => {
      const promise = new Promise<{
        ok: boolean;
        customFields?: CherryHubAdminApi.CustomFormField[];
      }>((resolve, reject) => {
        setActivePrompt({
          resolve,
          reject,
          formFields,
          action,
        });
      }).then((result) => {
        return result;
      });

      return promise;
    },
    [setActivePrompt]
  );

  return { modalElement, showCustomFieldModal };
}
